// Create first node
// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// api
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import {
  useDeleteEnergyGroupMutation,
  useGetEnergyGroupQuery,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { WritableOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// components
import CreateFirstNodeForm from '@/features/energyxplorer/forms/CreateFirstNodeForm';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/deepdive/$energyGroupId/create-first-node',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: { hideEnergyAnalysisAppBar: true },
});

function RouteComponent() {
  const { t } = useTranslation('energyxplorer');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { accountId, energyGroupId } = useParams({ strict: false });

  const { data: energyGroup } = useGetEnergyGroupQuery(
    { groupId: Number(energyGroupId) },
    { skip: !energyGroupId },
  );

  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${accountId})` },
    {
      skip: !accountId,
    },
  );

  const [deleteEnergyGroup, { isLoading: isDeleting }] =
    useDeleteEnergyGroupMutation();

  const handleSuccess = () => {
    dispatch(
      showSnackbar({
        message: 'Node oprettet succesfuldt',
        severity: 'success',
      }),
    );
    navigate({ to: `/$accountId/energyxplorer/deepdive/${energyGroupId}` });
  };

  const handleDelete = async () => {
    if (!energyGroupId) return;

    const result = await deleteEnergyGroup({ groupId: Number(energyGroupId) });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('energy_group_dialogs.create.messages.delete_success'),
          severity: 'success',
        }),
      );
      navigate({ to: '/$accountId/energyxplorer' });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <PageContainer maxWidth="md">
      <PageHeader
        title={t('nodes.create_node.title')}
        description={t('nodes.create_node.subheader')}
      />

      <WritableOnly>
        <CreateFirstNodeForm
          energyGroupId={Number(energyGroupId)}
          groupName={energyGroup?.name}
          units={units || []}
          onSuccess={handleSuccess}
          onDelete={handleDelete}
          isDeleting={isDeleting}
        />
      </WritableOnly>
    </PageContainer>
  );
}
